import React from "react";
import { AlertTriangle, Power, X, ShieldAlert, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

function FunnelDeactivationModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
}) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pulseGlow {
          0%,100% { box-shadow: 0 0 0 0 rgba(244,63,94,.4); }
          50% { box-shadow: 0 0 0 12px rgba(244,63,94,0); }
        }
        .modal-backdrop-anim { animation: fadeIn .25s ease-out; }
        .modal-panel-anim { animation: modalIn .4s cubic-bezier(.2,.9,.3,1.15); }
        .modal-pulse { animation: pulseGlow 2.2s ease-in-out infinite; }
      `}</style>

      <div
        className="modal-backdrop-anim fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 dark:!bg-black/70 backdrop-blur-md p-3 sm:p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="
            modal-panel-anim relative
            w-full max-w-md overflow-hidden
            rounded-3xl
            bg-white dark:!bg-slate-900
            border border-rose-500/20 dark:!border-rose-400/15
            shadow-[0_25px_70px_-20px_rgba(244,63,94,0.4)]
            dark:!shadow-[0_25px_70px_-20px_rgba(0,0,0,0.9)]
          "
          onClick={(e) => e.stopPropagation()}
          dir="rtl"
        >
          {/* خط بالایی قرمز */}
          <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-l from-transparent via-rose-400/90 to-transparent z-10" />

          {/* گلوه‌های تزئینی */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-rose-400/15 dark:!bg-rose-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-amber-300/15 dark:!bg-amber-400/10 blur-3xl" />

          {/* ============ Close Button ============ */}
          <button
            onClick={onClose}
            disabled={loading}
            aria-label={t("funnelDeactivationModal.close")}
            className="
              group absolute top-3 left-3 z-20
              h-9 w-9 flex items-center justify-center rounded-xl
              text-slate-400 dark:!text-slate-500
              bg-slate-100/60 hover:bg-rose-500/15 dark:!bg-white/5 dark:!hover:bg-rose-500/20
              hover:text-rose-500 dark:!hover:text-rose-400
              border border-slate-200/60 dark:!border-white/10 hover:border-rose-400/40
              transition-all duration-300
              hover:rotate-90 hover:scale-110 active:scale-95
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:rotate-0
            "
          >
            <X size={15} />
          </button>

          {/* ============ Body ============ */}
          <div className="relative p-6 sm:p-7">

            {/* Warning Icon */}
            <div className="mb-5 flex justify-center">
              <span className="relative inline-flex">
                <span className="modal-pulse absolute inset-0 rounded-full" />
                <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-rose-400/25 to-rose-600/15 border-2 border-rose-400/50 dark:!border-rose-400/30">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-700 shadow-[0_10px_30px_-8px_rgba(244,63,94,0.7)]">
                    <Power className="h-6 w-6 text-white drop-shadow" strokeWidth={2.5} />
                  </span>
                </span>
              </span>
            </div>

            {/* Title */}
            <h2 className="text-center text-lg sm:text-xl font-extrabold bg-gradient-to-l from-rose-600 to-red-500 dark:!from-rose-300 dark:!to-red-300 bg-clip-text text-transparent">
              {t("funnelDeactivationModal.title")}
            </h2>

            {/* Divider */}
            <div className="mx-auto mt-3 mb-4 h-px w-24 bg-gradient-to-l from-transparent via-rose-400/40 to-transparent" />

            {/* Description */}
            <p className="text-center text-xs sm:text-sm leading-7 text-slate-500 dark:!text-slate-400">
              {t("funnelDeactivationModal.descriptionLine1")}
              <br />
              {t("funnelDeactivationModal.descriptionLine2")}
            </p>

            {/* Warning note */}
            <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-rose-500/10 dark:!bg-rose-500/10 border border-rose-400/30 dark:!border-rose-400/20 px-3.5 py-3">
              <span className="shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 shadow-md">
                <AlertTriangle size={11} className="text-white" />
              </span>
              <p className="text-[10px] sm:text-[11px] font-medium text-rose-700 dark:!text-rose-200 leading-6">
                {t("funnelDeactivationModal.warningNote")}
              </p>
            </div>

            {/* ============ Actions ============ */}
            <div className="mt-6 flex gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="
                  flex-1 rounded-xl px-4 py-2.5
                  text-xs sm:text-sm font-bold
                  text-slate-700 dark:!text-slate-200
                  bg-white/70 dark:!bg-white/5
                  border border-slate-200 dark:!border-slate-700
                  hover:border-amber-300/60 hover:bg-amber-300/10
                  transition-all duration-200
                  hover:scale-[1.02] active:scale-95
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                "
              >
                {t("funnelDeactivationModal.cancel")}
              </button>

              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className="
                  group relative overflow-hidden flex-1
                  flex items-center justify-center gap-2
                  rounded-xl px-4 py-2.5
                  text-xs sm:text-sm font-bold text-white
                  bg-gradient-to-l from-rose-700 via-rose-600 to-rose-500
                  shadow-[0_8px_24px_-8px_rgba(244,63,94,0.8)]
                  hover:from-rose-600 hover:via-rose-500 hover:to-rose-400
                  hover:shadow-[0_12px_32px_-8px_rgba(244,63,94,1)]
                  transition-all duration-300
                  hover:scale-[1.02] active:scale-[0.98]
                  disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                "
              >
                {!loading && (
                  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                )}

                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>{t("funnelDeactivationModal.confirming")}</span>
                  </>
                ) : (
                  <>
                    <Power size={14} className="relative" />
                    <span className="relative">{t("funnelDeactivationModal.confirm")}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FunnelDeactivationModal;